import { Box, Typography } from "@mui/material";

import { theme } from "@/src/theme/theme";

import {
  type DealerLogo,
  getLogoUrl,
  getResponsivePadding
} from "@/src/utils/dealerLogoUtils";

interface DealerLogoCardProps {
  category: "primary" | "secondary";
  logo: DealerLogo;
}

const LOGO_SIZE = { xs: 80, sm: 100 };

// `logo.url` is already limited to http(s) by `sanitizeDealerLogo`. A real
// link (instead of window.open) keeps it crawlable in the prerendered HTML and
// `rel="noopener"` stops the dealer site from reaching back into this tab.
const getLinkProps = (url?: string) =>
  url
    ? {
        component: "a" as const,
        href: url,
        target: "_blank",
        rel: "noopener"
      }
    : {};

const DealerLogoCard = ({ category, logo }: DealerLogoCardProps) => {
  const { url } = logo;

  return (
    <Box
      {...getLinkProps(url)}
      data-dealer-logo=""
      sx={{
        width: { xs: "100%", sm: "45%" },
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        border: "1px solid",
        borderColor: theme.palette.divider,
        borderRadius: "10px",
        m: 2,
        textAlign: "center",
        textDecoration: "none",
        color: "inherit",
        boxShadow: (theme) => `0 4px 8px 0 ${theme.palette.misc.outlineBorder}`,
        transition: "0.3s",
        "&:hover": {
          boxShadow: (theme) =>
            `0 8px 16px 0 ${theme.palette.misc.outlineBorder}`
        },
        "&:focus-visible": {
          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
          outlineOffset: "2px"
        },
        cursor: url ? "pointer" : "default"
      }}
    >
      <Box
        sx={{
          width: LOGO_SIZE,
          height: LOGO_SIZE,
          mr: { xs: 1, sm: 3 },
          p: getResponsivePadding(logo.hasCustomPadding),
          backgroundColor: theme.palette.background.paper,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <img
          src={getLogoUrl(category, logo.fileName)}
          alt={logo.alt || logo.businessName}
          width={LOGO_SIZE.sm}
          height={LOGO_SIZE.sm}
          loading="lazy"
          decoding="async"
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      </Box>
      <Box sx={{ alignItems: "center", display: "flex" }}>
        <Typography variant="body1">{logo.businessName}</Typography>
      </Box>
    </Box>
  );
};

export default DealerLogoCard;
